import { FiCalendar, FiChevronLeft, FiGift, FiUser } from 'react-icons/fi';
import { TbCrown, TbUsers } from 'react-icons/tb';

import { imageVariantLink } from '@/common/media';
import { Avatar } from '@/components';
import { useNoYachtFallback } from '@/hooks/useNoYachtFallback';

import {
  fmtDate,
  isManuallyGranted,
  isPaidPlan,
  planName,
  renewalOf,
  statusView,
} from './subscription-display';
import s from './SubscriptionSettingsPage.module.scss';
import type { ManagedVesselSubscription } from './useManagedVesselSubscriptions';
import { useVesselSeats } from './useVesselSeats';
import type { SeatPoolState } from './useVesselSeats';
import { SEAT_POOL_LABEL, displayName } from './vessel-seats';
import type { SeatPerson, SeatPool } from './vessel-seats';

const AVATAR_SIZE = 30;

const PoolIcon = ({ pool }: { pool: SeatPool }) =>
  pool === 'management' ? <TbCrown /> : <TbUsers />;

/** One occupied seat. Titled so a face alone is still identifiable. */
const SeatAvatar = ({ person }: { person: SeatPerson }) => {
  const name = displayName(person);
  return (
    <div className={s.seat} title={name}>
      <Avatar
        src={person.avatar}
        firstName={person.firstName}
        lastName={person.lastName}
        size={AVATAR_SIZE}
        alt={name}
      />
    </div>
  );
};

/**
 * The management seat kept for an owner who has not joined yet. Paid for and empty like the
 * slots beside it, but spoken for, so it is marked rather than left looking like a seat
 * nobody got round to filling.
 */
const HeldSeat = () => (
  <div className={s.seatHeld} title="Held for the owner being added">
    <TbCrown />
  </div>
);

/**
 * A seat pool as faces rather than a bar: one avatar per occupied seat, one dashed slot per
 * free one, and the count alongside. Read-only - the portal offers the first free slot as an
 * add button, this view does not.
 */
const SeatRow = ({ state }: { state: SeatPoolState }) => {
  const { pool, capacity, occupants, heldForOwner } = state;
  // A seat held for an owner still to come is neither occupied nor open, so it is taken off
  // both counts and drawn on its own.
  const free = Math.max(0, capacity - occupants.length - heldForOwner);

  const sub = () => {
    if (capacity === 0) return 'No seats on this plan';
    const open = free === 0 ? 'all others in use' : `${free} ${free === 1 ? 'seat' : 'seats'} open`;
    if (heldForOwner > 0) return `1 held for the owner · ${open}`;
    return free === 0 ? 'All seats in use' : open;
  };

  return (
    <div className={s.row}>
      <div className={s.rowIcon}>
        <PoolIcon pool={pool} />
      </div>
      <div className={s.rowText}>
        <div className={s.rowTitle}>{SEAT_POOL_LABEL[pool]}</div>
        <div className={s.rowSub}>{sub()}</div>
        {capacity > 0 && (
          <div className={s.seats}>
            {occupants.map((person) => (
              <SeatAvatar key={person.personId} person={person} />
            ))}
            {Array.from({ length: heldForOwner }, (_, index) => (
              <HeldSeat key={`held-${index}`} />
            ))}
            {Array.from({ length: free }, (_, index) => (
              <div key={`empty-${index}`} className={s.seatEmpty} aria-hidden />
            ))}
          </div>
        )}
      </div>
      <span className={s.countChip}>
        {occupants.length}/{capacity}
      </span>
    </div>
  );
};

export function VesselDetail({
  item,
  onBack,
}: {
  item: ManagedVesselSubscription;
  onBack: () => void;
}) {
  const NoYachtFallback = useNoYachtFallback();
  const { vessel, subscription, isPayer, canTakeOver } = item;
  const status = statusView(subscription);
  const renewal = renewalOf(subscription);
  const seats = useVesselSeats(item);
  // An admin granted the plan: nobody's card is behind it, and nothing about it can be changed here.
  const granted = isManuallyGranted(subscription);
  // The plan is real, and it is on somebody else's card.
  const paidByAnother = isPaidPlan(subscription) && !granted && !isPayer;

  const seatRows = () => {
    if (seats.isLoading) return <div className={s.empty}>Loading seats…</div>;
    if (seats.error)
      return <div className={s.empty}>Couldn’t load who holds a seat. Please reload the page.</div>;
    // Covers no subscription row, a dead one, and harbor - the plan line above already says
    // which, so this only states the consequence.
    if (seats.capacity === 0) return <div className={s.empty}>This plan includes no seats.</div>;

    // A pool the tier does not sell is left out entirely rather than shown as 0/0.
    return seats.pools
      .filter((pool) => pool.capacity > 0)
      .map((pool) => <SeatRow key={pool.pool} state={pool} />);
  };

  return (
    <>
      <button type="button" className={s.backBtn} onClick={onBack}>
        <FiChevronLeft /> Subscription Management
      </button>

      <header className={s.detailHeader}>
        <Avatar
          src={imageVariantLink(vessel.image, 38) || NoYachtFallback}
          firstName={vessel.vessel_name}
          size={38}
        />
        <div className={s.detailHeaderText}>
          <h2 className={s.detailTitle}>
            <span className={s.titleWithPlan}>
              {vessel.vessel_name}
              <span className={s.titlePlan}>{planName(subscription)}</span>
            </span>
          </h2>
        </div>
        <span className={`${s.statusChip} ${s[`status_${status.tone}`]}`}>{status.label}</span>
      </header>

      {/*
        The dates below belong to whoever bought the plan, and that need not be whoever is
        reading them: a captain can buy a vessel's plan and keeps paying for it after an owner
        comes aboard. The owner takes it over by buying it themselves, which happens in the
        billing portal behind "Manage account online" - so they are pointed at it, and anybody
        else is only told whose bill this is not.
      */}
      {granted && (
        <div className={s.group}>
          <div className={s.row}>
            <div className={s.rowIcon}>
              <FiGift />
            </div>
            <div className={s.rowText}>
              <div className={s.rowTitle}>Lifetime Plan</div>
              <div className={s.rowSub}>
                We granted this plan, so it never expires and we never bill for it. Please contact
                support to change it.
              </div>
            </div>
          </div>
        </div>
      )}

      {paidByAnother && (
        <div className={s.group}>
          <div className={s.row}>
            <div className={s.rowIcon}>
              <FiUser />
            </div>
            <div className={s.rowText}>
              <div className={s.rowTitle}>Paid by somebody else</div>
              <div className={s.rowSub}>
                {canTakeOver
                  ? 'You can take over this plan from Manage Account Online. Their plan stays as it is until your payment clears.'
                  : 'This plan is on another person’s card, so you don’t pay for it.'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Only stated when the subscription carries a date - a free plan has none. */}
      {renewal.at != null && (
        <div className={s.group}>
          <div className={s.row}>
            <div className={s.rowIcon}>
              <FiCalendar />
            </div>
            <div className={s.rowText}>
              <div className={s.rowTitle}>
                {renewal.kind === 'ends' ? 'Your access will end on' : 'Your plan renews on'}
              </div>
              <div className={s.rowSub}>{fmtDate(renewal.at)}</div>
            </div>
          </div>
        </div>
      )}

      <p className={s.groupLabel}>
        Seats
        {seats.capacity > 0 && ` · ${seats.occupied}/${seats.capacity} assigned`}
      </p>
      <div className={s.group}>{seatRows()}</div>
    </>
  );
}
